import React, { useState, useEffect } from 'react';
import { Input, Tabs, Space, Badge } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;

const LANGUAGES = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'ar', label: 'العربية', flag: '🇸🇦' },
];

/**
 * TranslatableInput — a reusable input component that stores translations
 * for multiple languages as an object: { en: "...", ar: "..." }
 *
 * Props:
 *  - value: { en: string, ar: string } (or any LANGUAGES codes)
 *  - onChange: (value: object) => void
 *  - placeholder: string (optional)
 *  - disabled: boolean (optional)
 *  - status: "" | "error" (optional)
 *  - label: string (optional, shown above tabs)
 *  - multiline: boolean (optional, renders a TextArea instead of Input)
 *  - maxLength: number (optional, only applies when multiline)
 */
export default function TranslatableInput({
    value = {},
    onChange,
    placeholder = '',
    disabled = false,
    status = '',
    multiline = false,
    textarea = false,
    autoSize,        
    maxLength,
}) {
    const { i18n } = useTranslation();
    const [activeKey, setActiveKey] = useState(i18n.language || LANGUAGES[0].code);

    useEffect(() => {
        setActiveKey(i18n.language);
    }, [i18n.language]);

    const handleChange = (langCode, newValue) => {
        onChange({ ...value, [langCode]: newValue });
    };

    const getPlaceholder = (langCode) => {
        if (typeof placeholder === 'object' && placeholder !== null) {
            return placeholder[langCode] || '';
        }
        return placeholder;
    };

    const isTextArea = multiline || textarea;

    const tabItems = LANGUAGES.map((lang) => {
        const hasContent = !!(value[lang.code] && value[lang.code].trim());
        return {
            key: lang.code,
            label: (
                <Space size={4} align="center">
                    <span style={{ fontSize: 13 }}>{lang.flag}</span>
                    <span style={{ fontSize: 12 }}>{lang.label}</span>
                    {!hasContent && (
                        <Badge status="default" style={{ marginLeft: 2 }} />
                    )}
                    {hasContent && (
                        <Badge status="success" style={{ marginLeft: 2 }} />
                    )}
                </Space>
            ),
            children: isTextArea ? (
                <TextArea
                    value={value[lang.code] || ''}
                    onChange={(e) => handleChange(lang.code, e.target.value)}
                    placeholder={getPlaceholder(lang.code)}
                    disabled={disabled}
                    status={status}
                    showCount  
                    allowClear 
                    maxLength={maxLength}
                    autoSize={autoSize} 
                    style={{ direction: lang.code === 'ar' ? 'rtl' : 'ltr' }}
                />
            ) : (
                <Input
                    value={value[lang.code] || ''}
                    onChange={(e) => handleChange(lang.code, e.target.value)}
                    placeholder={getPlaceholder(lang.code)}
                    disabled={disabled}
                    status={status}
                    size="large"
                    style={{ direction: lang.code === 'ar' ? 'rtl' : 'ltr' }}
                />
            ),
        };
    });

    return (
        <Tabs
            activeKey={activeKey}
            onChange={setActiveKey}
            items={tabItems}
            size="small"
            tabBarGutter={16}
            style={{ marginBottom: 0 }}
            tabBarStyle={{ marginBottom: 4 }}
        />
    );
}